import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ApiClient from '../../../methods/api/apiClient';
import loader from '../../../methods/loader';
import methodModel from '../../../methods/methods';
import { logout } from '../../../actions/user';
import { LuEye, LuEyeOff } from 'react-icons/lu';

// ─── Password strength rules ───────────────────────────────────────────────
const PASSWORD_RULES = [
  { key: 'minLength', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { key: 'uppercase', label: 'At least 1 uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { key: 'lowercase', label: 'At least 1 lowercase letter', test: (v) => /[a-z]/.test(v) },
  { key: 'number', label: 'At least 1 number', test: (v) => /[0-9]/.test(v) },
  { key: 'special', label: 'At least 1 special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const validatePassword = (v) => PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(v) }));
const isPasswordValid = (v) => PASSWORD_RULES.every((r) => r.test(v));

function ChangePassword(p) {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const history = useNavigate();

  const [form, setForm] = useState({
    confirmPassword: '',
    currentPassword: '',
    newPassword: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [eyes, setEyes] = useState({
    password: false,
    confirmPassword: false,
    currentPassword: false,
  });

  const formValidation = [
    { key: 'confirmPassword', minLength: 8, confirmMatch: ['confirmPassword', 'newPassword'] },
    { key: 'currentPassword', minLength: 8 },
    { key: 'newPassword', minLength: 8 },
  ];

  // Derived — recalculated every render, no extra state
  const passwordRules = validatePassword(form.newPassword);
  const passwordValid = isPasswordValid(form.newPassword);
  const confirmMatch = form.confirmPassword === form.newPassword;
  const passedCount = passwordRules.filter((r) => r.passed).length;

  const strengthLabel =
    passedCount <= 1 ? 'Weak' : passedCount <= 3 ? 'Fair' : passedCount === 4 ? 'Good' : 'Strong';
  const strengthColor =
    passedCount <= 1 ? '#ef4444' : passedCount <= 3 ? '#f59e0b' : passedCount === 4 ? '#3b82f6' : '#22c55e';

  const getError = (key) => methodModel.getError(key, form, formValidation);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!passwordValid) return;
    if (!confirmMatch) return;
    const invalid = methodModel.getFormError(formValidation, form);
    if (invalid) return;
    loader(true);
    ApiClient.put('user/change/password', {
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
      id: user?._id || user?.id,
    }).then((res) => {
      if (res.success) {
        dispatch(logout());
        localStorage.removeItem('token');
        localStorage.removeItem('persist:admin-app');
        history('/login');
      }
      loader(false);
    });
  };

  return (
    <div className="wrapper_section">
         <div className="flex items-center justify-between gap-2 mb-5">
        <h3 className="text-2xl font-semibold text-[#111827]">
           Change Password
        </h3>
      </div>


      <form onSubmit={handleSubmit}>
        <div className='bg-[#FAFAFA] rounded-[12px] p-5 sm:p-8'>
          <div className="grid grid-cols-12">
            <div className="col-span-12 xl:col-span-12 input_form bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888]">
              <div className="items-center">
                <div className="space-y-6">

                  {/* ── Current Password ── */}
                  <div className="flex flex-col lg:flex-row gap-4">
                    <label className="text-typo text-base font-medium w-96">
                      Current Password <span className="start">*</span>
                    </label>
                    <div className="w-full">
                      <div className="relative">
                        <input
                          type={eyes.currentPassword ? 'text' : 'password'}
                          className="relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 "
                          value={form.currentPassword}
                          maxLength="20"
                          placeholder="Enter current password"
                          onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                          required
                        />
                        <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                          <span onClick={() => setEyes({ ...eyes, currentPassword: !eyes.currentPassword })}>
                            {eyes.currentPassword
                              ? <LuEye className="text-gray-400" />
                              : <LuEyeOff className="text-gray-400" />}
                          </span>
                        </div>
                      </div>
                      {/* Fixed-height feedback — no layout shift */}
                      <div style={{ marginTop: '4px' }}>
                        {submitted && getError('currentPassword').invalid && (
                          <p style={{ fontSize: '12px', color: '#ef4444', margin: 0 }}>
                            Min length must be 8 characters long.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── New Password ── */}
                  <div className="flex flex-col lg:flex-row gap-4">
                    <label className="text-typo text-base font-medium w-96">
                      New Password <span className="start">*</span>
                    </label>

                    <div className="w-full">
                      <div className="relative">
                        <input
                          type={eyes.password ? 'text' : 'password'}
                          className="relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 "
                          value={form.newPassword}
                          placeholder="Enter new password"
                          maxLength={16}
                          onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                          required
                        />
                        <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                          <i onClick={() => setEyes({ ...eyes, password: !eyes.password })}>
                            {eyes.password
                              ? <LuEye className="text-gray-400" />
                              : <LuEyeOff className="text-gray-400" />}
                          </i>
                        </div>
                      </div>

                      {/*
                      Fixed-height block (118px) — ALWAYS in the DOM.
                      Strength bar + checklist fade in via opacity only.
                      Nothing below ever shifts position.
                    */}
                      <div style={{ paddingTop: '8px', overflow: 'hidden' }}>

                        {/* Strength bar */}
                        <div style={{
                          opacity: 1,
                          transition: 'opacity 0.2s',
                        }}>
                          <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <div
                                key={n}
                                style={{
                                  flex: 1,
                                  height: '4px',
                                  borderRadius: '9999px',
                                  background: n <= passedCount ? strengthColor : '#e5e7eb',
                                  transition: 'background 0.25s',
                                }}
                              />
                            ))}
                          </div>
                          <p style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: strengthColor,
                            margin: '0 0 6px 0',
                            transition: 'color 0.2s',
                          }}>
                            {strengthLabel}
                          </p>
                        </div>

                        {/* Rule checklist — always rendered, color transitions only */}
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          {passwordRules.map((rule) => (
                            <li
                              key={rule.key}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '11px',
                                lineHeight: '1.6',
                                color: form.newPassword.length === 0
                                  ? '#9ca3af'
                                  : rule.passed ? '#22c55e' : '#ef4444',
                                transition: 'color 0.2s',
                              }}
                            >
                              <span style={{ fontSize: '10px', width: '10px', flexShrink: 0 }}>
                                {form.newPassword.length === 0 ? '·' : rule.passed ? '✓' : '✗'}
                              </span>
                              {rule.label}
                            </li>
                          ))}
                        </ul>

                      </div>
                      {/* end fixed-height block */}

                      {submitted && !passwordValid && (
                        <p style={{ fontSize: '12px', color: '#ef4444', margin: '2px 0 0' }}>
                          Password does not meet all requirements.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* ── Confirm Password ── */}
                  <div className="flex flex-col lg:flex-row gap-4">
                    <label className="text-typo text-base font-medium w-96">
                      Confirm Password <span className="start">*</span>
                    </label>

                    <div className="w-full">
                      <div className="relative">
                        <input
                          type={eyes.confirmPassword ? 'text' : 'password'}
                          className="relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 "
                          placeholder="Enter confirm password"
                          value={form.confirmPassword}
                          maxLength={16}
                          onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                          required
                        />
                        <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                          <i onClick={() => setEyes({ ...eyes, confirmPassword: !eyes.confirmPassword })}>
                            {eyes.confirmPassword
                              ? <LuEye className="text-gray-400" />
                              : <LuEyeOff className="text-gray-400" />}
                          </i>
                        </div>
                      </div>

                      {/* Fixed-height confirm feedback — no reflow */}
                      <div style={{ marginTop: '4px' }}>
                        {form.confirmPassword.length > 0 ? (
                          <p style={{
                            fontSize: '12px',
                            color: confirmMatch ? '#22c55e' : '#ef4444',
                            transition: 'color 0.2s',
                            margin: 0,
                          }}>
                            {confirmMatch ? '✓ Passwords match' : '✗ Passwords does not match'}
                          </p>
                        ) : submitted ? (
                          <p style={{ fontSize: '12px', color: '#ef4444', margin: 0 }}>
                            Please confirm your password.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
                          <div className="flex gap-2 justify-end mt-6">
                    <button
                      type="submit"
                      className="bg-primary leading-10 h-10 flex justify-center  items-center shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-full gap-1 cursor-pointer min-w-[94px]"
                    >
                      Update
                    </button>
                  </div>
      </form>
    </div>
  );
}

export default ChangePassword;